import React, { useState, useEffect, useRef } from 'react';
import { DropdownButtonType } from 'types/ButtonTypes';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import '../../style/scss/button.scss';

const DropdownButton: React.FC<DropdownButtonType> = ({
    items, 
    label, 
    icon,
    tooltipText,
    size = 'medium', 
    loading = false, 
    type = 'button', 
    activeItem,
    onlyViewRestriction,
    customClass,
    buttonType = 'secondary',
    menuAlignment = 'left',
    menuDirection = 'down'
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const toggleDropdown = () => setIsOpen(!isOpen);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Make the button invisible if it is restricted for only_view users
    const userHasOnlyViewRight = !useAllowedRight('only_view')
    const isVisible = onlyViewRestriction ? userHasOnlyViewRight : true;

    // Set the size, button and text loading classes
    const buttonSizeClass = `button-${size}`;
    const buttonLoadingClass = loading ? 'button-loading' : '';
    const textLoadingClass = loading ? 'text-hidden' : '';

    // An icon without label renders as an icon only button without caret
    const isIconOnly = Boolean(icon) && !label;

    // Give the loader icon a delay, so it looks smoother
    const [showLoaderWithDelay, setShowLoaderWithDelay] = useState(false);
    useEffect(() => {
        let timer: number;
        if (loading) {
            timer = window.setTimeout(() => setShowLoaderWithDelay(true), 150)
        } else {
            setShowLoaderWithDelay(false);
        }
        return () => clearTimeout(timer);
    }, [loading]);

    // Close the dropdown when clicking outside it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        isVisible ? (
            <div className={`dropdown-button ${menuAlignment === 'right' ? 'menu-right' : ''} ${menuDirection === 'up' ? 'menu-up' : ''}`} ref={dropdownRef}>
                <button type={type}
                        onClick={toggleDropdown}
                        className={`button ${buttonSizeClass} button-${buttonType} ${isIconOnly ? 'dropdown-icon-button' : ''} ${customClass ?? ''} ${isOpen ? 'button-active' : ''} ${buttonLoadingClass}`}
                        style={{ position: 'relative' }}
                        aria-label={tooltipText ? t(tooltipText) : undefined}
                        disabled={loading}>
                    {isIconOnly ? (
                        <span className={`button-text ${textLoadingClass}`}>
                            {icon}
                        </span>
                    ) : (
                        <span className={`button-text ${textLoadingClass}`}>
                            {t(label ?? '')}
                        </span>
                    )}
                    {showLoaderWithDelay && 
                        <div className='loader-container'>
                            <div className='button-loader' />
                        </div>
                    }
                    {!isIconOnly && (
                        <FontAwesomeIcon
                            icon={!isOpen ? faCaretDown : faCaretUp}
                            style={{ marginLeft: '0.3rem', paddingBottom: '0.025rem' }}
                        />
                    )}
                </button>
                {isOpen && (
                    <div className='dropdown-button-menu'>
                        {items.map((item, index) => (
                            <div key={index} className={`dropdown-button-item ${item.danger ? 'danger' : ''}`} onClick={() => { toggleDropdown(); item.onClick() } } >
                                {t(item.label)}
                                {item.label === activeItem && (
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className='filter-dropdown-icon' />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ) : null
    );
};

export default DropdownButton;